import {
  Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy,
  ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef,
  ViewChild, ElementRef, AfterViewInit, SimpleChanges, Optional, ComponentRef, NgZone
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { SafeHtml } from '@angular/platform-browser';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import type { ClassicEditor, EditorConfig } from 'ckeditor5';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { SharedModule } from 'src/app/shared/shared.module';
import { TemplateJson, TemplatePrintOptions, TemplateContext, normalizeLegacyMargins } from 'src/app/models/reportTemplate.model';
import { ReportTemplateValidationService, ValidationIssue } from 'src/app/services/report-template-validation.service';
import { carryForwardUnresolvedIssues, mergeUnresolvedIssues } from './unresolved-placeholder-issues.helper';
import { ValidationBannerComponent } from './validation-banner/validation-banner.component';
import { PlaceholderPaletteComponent, PaletteField, PaletteFieldGroup } from './placeholder-palette/placeholder-palette.component';
import { WidgetPropertyPanelComponent, WidgetPropertyChange } from './widget-property-panel/widget-property-panel.component';
import { EditorTourComponent, EditorTourStep } from './editor-tour/editor-tour.component';
import { REPORT_TEMPLATE_EDITOR_TOUR_STEPS } from './editor-tour/editor-tour.steps';
import { getCKEditorPluginConfig } from 'src/app/shared/rich-text-editor/ckeditor-plugin-config.helper';
import { stripChipMarkupForStorage, preprocessHtmlForEditor, insertPlaceholderAtCursor, getCursorLoopScope, FrameLabels } from 'src/app/shared/rich-text-editor/placeholder-chip.plugin';
import { TranslateService } from '@ngx-translate/core';
import { TemplateMapperRegistry } from 'src/app/services/template-mappers/template-mapper-registry';
import { RuntimeEntityGroupConfig } from 'src/app/services/template-mappers/base-template-mapper';
import { TemplateFieldResolverService, ResolvedPalette } from 'src/app/services/template-mappers/template-field-resolver.service';
import { PdfGeneratorService } from '../report-generation-popup/services/pdf-generator.service';
import { getWidgetsForEntityType, WidgetLoopScope } from 'src/app/shared/widget-registry';
import { EditorTab, CKEDITOR_DOM } from './report-template-editor.constants';
import { getPageWidthPx, marginsToPaddingPx, PREVIEW_MIN_PADDING_PX } from '../report-generation-popup/services/preview-css.constants';
import { computePreviewZoom } from './preview-zoom.helper';
import { loadCKEditor } from 'src/app/shared/rich-text-editor/ckeditor-loader.helper';
import { environment } from 'src/environments/environment';
import { TemplateProcessorService } from '../report-generation-popup/services/template-processor.service';
import { ToastService } from 'src/app/services/toast.service';

const PREVIEW_PANE_PADDING_PX = 12;

export interface EditorSavePayload {
  layoutHtml: string;
  printOptions: TemplatePrintOptions;
}

export interface ReportEditorConfig {
  templateJson: TemplateJson;
  entityType: string;
  templateContext?: TemplateContext;
  htmlPreview: SafeHtml;
  showLivePreview: boolean;
}

export enum ReportEditorEventKind {
  Saved = 'saved',
  Discarded = 'discarded',
  GeneratePdf = 'generatePdf',
  ContentChange = 'contentChange',
  TogglePreview = 'togglePreview',
  PrintOptionsChange = 'printOptionsChange'
}

export type ReportEditorEvent =
  | { kind: ReportEditorEventKind.Saved; payload: EditorSavePayload }
  | { kind: ReportEditorEventKind.Discarded }
  | { kind: ReportEditorEventKind.GeneratePdf }
  | { kind: ReportEditorEventKind.ContentChange; html: string }
  | { kind: ReportEditorEventKind.TogglePreview }
  | { kind: ReportEditorEventKind.PrintOptionsChange; printOptions: TemplatePrintOptions };

@Component({
    standalone: true,
    selector: 'app-report-template-editor',
    imports: [FormsModule, CKEditorModule, SharedModule, ValidationBannerComponent, PlaceholderPaletteComponent, WidgetPropertyPanelComponent, EditorTourComponent],
    templateUrl: './report-template-editor.component.html',
    styleUrls: ['./report-template-editor.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportTemplateEditorComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() config!: ReportEditorConfig;
  @Output() event = new EventEmitter<ReportEditorEvent>();
  @ViewChild('previewContentEl') previewContentEl?: ElementRef<HTMLElement>;
  @ViewChild('previewPageEl') previewPageEl?: ElementRef<HTMLElement>;
  @ViewChild('tour') tour?: EditorTourComponent;

  readonly tourSteps: EditorTourStep[] = REPORT_TEMPLATE_EDITOR_TOUR_STEPS;

  private readonly destroy$ = new Subject<void>();
  private readonly htmlChange$ = new Subject<string>();
  private ckFlexObserver: MutationObserver | null = null;
  private previewResizeObserver: ResizeObserver | null = null;
  private loopScopeMap = new Map<string, WidgetLoopScope>();
  hasLoopScopes = false;

  Editor: typeof ClassicEditor | null = null;
  editorConfig: EditorConfig | null = null;
  editorHtml = '';
  validationIssues: ValidationIssue[] = [];
  paletteFields: PaletteField[] = [];
  paletteRuntimeGroups: readonly RuntimeEntityGroupConfig[] = [];
  contextFields: PaletteField[] = [];
  contextLabel = '';
  selectedWidgetTag: string | null = null;
  widgetProperties: Record<string, boolean | string | number> = {};
  localPrintOptions: TemplatePrintOptions | null = null;
  readonly EditorTab = EditorTab;
  activeTab: EditorTab = EditorTab.Palette;
  isSaving = false;
  sidebarCollapsed = false;
  private editorInstance: ClassicEditor | null = null;
  private destroyed = false;

  constructor(
    private validationService: ReportTemplateValidationService,
    private cdr: ChangeDetectorRef,
    private mapperRegistry: TemplateMapperRegistry,
    private fieldResolver: TemplateFieldResolverService,
    private translate: TranslateService,
    private zone: NgZone,
    @Optional() private pdfGenerator: PdfGeneratorService | null,
    private templateProcessor: TemplateProcessorService,
    private toastService: ToastService
  ) {}

  private frameLabels(): FrameLabels {
    return {
      repeatsForEach: this.translate.instant('Repeats for each'),
      shownOnlyWhen: this.translate.instant('shown only when')
    };
  }

  async ngOnInit(): Promise<void> {
    const templateJson = this.config.templateJson;
    const userInputDefaults: Record<string, string> = {};
    for (const [key, input] of Object.entries(templateJson.declaredFields?.userInputs || {})) {
      userInputDefaults[key] = typeof input.default === 'string' ? input.default : input.label;
    }
    const snapshotMap = await this.captureWidgetSnapshots(templateJson.layoutHtml);
    if (this.destroyed) return;
    this.editorHtml = preprocessHtmlForEditor(templateJson.layoutHtml, userInputDefaults, snapshotMap, this.frameLabels());
    this.localPrintOptions = {
      ...templateJson.printOptions,
      margins: normalizeLegacyMargins(templateJson.printOptions?.margins)
    };
    this.loadPaletteFields();

    this.htmlChange$.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(html => {
      this.runValidation(html);
      this.event.emit({ kind: ReportEditorEventKind.ContentChange, html });
      this.cdr.markForCheck();
    });

    await this.initEditor();
    if (this.destroyed) return;
    this.runValidation(this.editorHtml);
    this.cdr.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const cfgChange = changes['config'];
    if (!cfgChange) return;
    const prev = cfgChange.previousValue as ReportEditorConfig | undefined;
    const curr = cfgChange.currentValue as ReportEditorConfig;

    if (prev && prev.htmlPreview !== curr.htmlPreview) {
      Promise.resolve().then(() => this.applyPreviewScale());
    }
    if (curr.showLivePreview === true && prev?.showLivePreview !== true) {
      Promise.resolve().then(() => this.setupPreviewScaling());
    }
  }

  ngAfterViewInit(): void {
    this.setupPreviewScaling();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    this.ckFlexObserver?.disconnect();
    this.previewResizeObserver?.disconnect();
    this.editorInstance = null;
  }

  private setupPreviewScaling(): void {
    if (this.destroyed) return;
    const el = this.previewContentEl?.nativeElement;
    if (!el) return;
    this.previewResizeObserver?.disconnect();
    this.previewResizeObserver = new ResizeObserver(() => this.applyPreviewScale());
    this.previewResizeObserver.observe(el);
    this.applyPreviewScale();
  }

  private applyPreviewScale(): void {
    if (this.destroyed) return;
    const pane = this.previewContentEl?.nativeElement;
    const page = this.previewPageEl?.nativeElement;
    if (!pane || !page) return;

    const isLandscape = this.localPrintOptions?.pageOrientation === 'landscape';
    const pageWidth = getPageWidthPx(this.localPrintOptions?.pageSize, isLandscape);
    const newPadding = marginsToPaddingPx(this.localPrintOptions?.margins, PREVIEW_MIN_PADDING_PX);
    const paneWidth = pane.clientWidth - PREVIEW_PANE_PADDING_PX * 2;
    const newZoom = computePreviewZoom(paneWidth, pageWidth);

    page.style.width = `${pageWidth}px`;
    if (page.style.padding !== newPadding) {
      page.style.padding = newPadding;
    }
    const currentZoom = parseFloat(page.style.getPropertyValue('zoom') || '1');
    if (Math.abs(newZoom - currentZoom) > 0.005) {
      page.style.setProperty('zoom', `${newZoom}`);
    }
  }

  private async initEditor(): Promise<void> {
    const ck = await loadCKEditor();
    const resolveLabel = (path: string): string => this.pathToLabel(path);
    const pluginConfig = getCKEditorPluginConfig(ck, true, resolveLabel, this.frameLabels());

    this.editorConfig = {
      licenseKey: pluginConfig.licenseKey ?? 'GPL',
      plugins: pluginConfig.plugins,
      toolbar: {
        items: pluginConfig.toolbar,
        shouldNotGroupWhenFull: false
      },
      htmlSupport: {
        allow: [{ name: /.*/, attributes: true, classes: true, styles: true }]
      },
      table: {
        contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells',
                         'tableProperties', 'tableCellProperties', 'toggleTableCaption', 'tableColumnResize'],
        tableProperties: { alignment: { useInlineStyles: true } }
      },
      image: {
        toolbar: ['imageTextAlternative', 'toggleImageCaption']
      },
      link: { addTargetToExternalLinks: true, defaultProtocol: 'https://' },
    };

    this.Editor = ck.ClassicEditor;
    this.cdr.markForCheck();
  }

  private loadPaletteFields(): void {
    this.loopScopeMap = new Map<string, WidgetLoopScope>();
    getWidgetsForEntityType(this.config.entityType)
        .flatMap(w => w.loopScopes ?? [])
        .forEach(s => this.loopScopeMap.set(s.loopExpr, s));
    this.hasLoopScopes = this.loopScopeMap.size > 0;

    const mapper = this.mapperRegistry.getMapper(this.config.entityType);
    if (!mapper) return;

    this.fieldResolver.resolveFields(mapper)
        .pipe(takeUntil(this.destroy$))
        .subscribe((palette: ResolvedPalette) => {
            const runtimeGroupConfigs: RuntimeEntityGroupConfig[] = palette.groups.map(g => ({
                key: g.key,
                label: g.label,
                accent: g.accent,
                fields: g.fields.map(f => f.path.startsWith(`${g.key}.`) ? f.path.slice(g.key.length + 1) : f.path),
            }));
            this.paletteRuntimeGroups = runtimeGroupConfigs;

            const bound = this.config.templateJson.declaredFields?.bound || [];
            const imagePathSet = new Set<string>(
                palette.groups.flatMap(g => g.fields.filter(f => f.isImage).map(f => f.path))
            );

            this.paletteFields = bound.map(path => ({
                path,
                label: this.pathToLabel(path, palette),
                group: PaletteFieldGroup.Bound,
                hidden: imagePathSet.has(path),
            }));

            const boundPaths = new Set(bound);
            for (const group of palette.groups) {
                for (const field of group.fields) {
                    if (!boundPaths.has(field.path)) {
                        this.paletteFields.push({
                            path: field.path,
                            label: field.label,
                            group: PaletteFieldGroup.Runtime,
                            hidden: field.isImage === true,
                            entityGroup: group.key,
                        });
                    }
                }
            }

            for (const synth of palette.synthetic) {
                if (!boundPaths.has(synth.path)) {
                    this.paletteFields.push({
                        path: synth.path,
                        label: synth.label,
                        group: PaletteFieldGroup.Runtime,
                        hidden: synth.isImage === true,
                    });
                }
            }

            this.runValidation(this.editorHtml);
            this.cdr.markForCheck();
        });
  }

  private async captureWidgetSnapshots(layoutHtml: string): Promise<Map<string, string[]> | undefined> {
    const context = this.config.templateContext;
    const entityType = this.config.entityType;
    if (!this.pdfGenerator || !context) return undefined;

    const presentTags = getWidgetsForEntityType(entityType)
      .map(w => w.tagName)
      .filter(tag => layoutHtml.includes(`<${tag}`));

    if (presentTags.length === 0) return undefined;

    const parsed = new DOMParser().parseFromString(layoutHtml, 'text/html');
    const tempContainer = document.createElement('div');
    tempContainer.style.visibility = 'hidden';
    tempContainer.style.position = 'fixed';
    tempContainer.style.top = '-9999px';
    Array.from(parsed.body.childNodes).forEach(node => tempContainer.appendChild(node));
    document.body.appendChild(tempContainer);

    let refs: ComponentRef<unknown>[] = [];
    try {
      refs = await this.pdfGenerator.injectWidgets(
        tempContainer, entityType, context, false
      );
      await this.pdfGenerator.waitForWidgets(refs);

      const snapshotMap = new Map<string, string[]>();
      for (const tag of presentTags) {
        const snapshots = Array.from(tempContainer.querySelectorAll(tag)).map(el => el.innerHTML);
        if (snapshots.length > 0) {
          snapshotMap.set(tag, snapshots);
        }
      }
      return snapshotMap.size > 0 ? snapshotMap : undefined;
    } catch (error) {
      if (!environment.production) {
        console.warn('Widget snapshot capture failed; widgets start as empty editables', error);
      }
      return undefined;
    } finally {
      refs.forEach(ref => ref.destroy());
      document.body.removeChild(tempContainer);
    }
  }

  private pathToLabel(path: string, palette?: ResolvedPalette): string {
    if (palette) {
      for (const group of palette.groups) {
        const found = group.fields.find(f => f.path === path);
        if (found) return found.label;
      }
      const synth = palette.synthetic.find(f => f.path === path);
      if (synth) return synth.label;
    }
    const last = path.split('.').pop() || path;
    return last.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
  }

  private updateContextFields(): void {
    const scope = this.editorInstance ? getCursorLoopScope(this.editorInstance) : null;
    const group = scope ? this.loopScopeMap.get(scope) : undefined;
    this.contextFields = group
        ? group.fields.map(f => ({
            path: `this.${f}`,
            label: this.pathToLabel(`this.${f}`),
            group: PaletteFieldGroup.Context
          }))
        : [];
    this.contextLabel = group?.label ?? '';
    this.cdr.markForCheck();
  }

  private runValidation(html: string): void {
    // Validate the stored form (chips stripped to raw tokens) so loop boundaries are
    // real [[#each]]/[[/each]] tokens — that is what the validator tracks block depth on,
    // and it is what actually gets persisted. Otherwise loop-local refs false-flag.
    const storageHtml = stripChipMarkupForStorage(html);
    const carried = carryForwardUnresolvedIssues(this.validationIssues, storageHtml);
    if (this.paletteFields.length === 0) {
      this.validationIssues = mergeUnresolvedIssues([], carried);
      return;
    }
    const knownPaths = this.paletteFields.map(f => f.path);
    this.validationIssues = mergeUnresolvedIssues(this.validationService.validate(storageHtml, knownPaths), carried);
  }

  onEditorReady(editor: ClassicEditor): void {
    this.editorInstance = editor;
    editor.model.document.selection.on('change:range', () => {
        this.zone.run(() => this.updateContextFields());
    });
    this.updateContextFields();

    const el = editor?.ui?.view?.editable?.element as HTMLElement | undefined;
    if (!el) return;

    const applyChain = (): void => {
      const editable = editor?.ui?.view?.editable?.element as HTMLElement | null;
      if (!editable) return;

      editable.style.flex = '1 1 0';
      editable.style.minHeight = '0';
      editable.style.maxHeight = 'none';
      editable.style.height = '100%';
      editable.style.overflowY = 'auto';
      editable.style.overflowX = 'hidden';

      const applyFlex = (node: HTMLElement | null): void => {
        if (!node) return;
        node.style.display = 'flex';
        node.style.flexDirection = 'column';
        node.style.flex = '1 1 0';
        node.style.minHeight = '0';
        node.style.height = '100%';
        node.style.overflow = 'hidden';
      };

      applyFlex(editable.closest<HTMLElement>(CKEDITOR_DOM.MAIN_SELECTOR));
      applyFlex(editable.closest<HTMLElement>(CKEDITOR_DOM.EDITOR_SELECTOR));
      applyFlex(editable.closest<HTMLElement>(CKEDITOR_DOM.EDITOR_ELEMENT_TAG));
      applyFlex(editable.closest<HTMLElement>(CKEDITOR_DOM.HOST_TAG));
    };

    applyChain();

    const host = el.closest<HTMLElement>(CKEDITOR_DOM.HOST_TAG);
    if (host) {
      this.ckFlexObserver?.disconnect();
      this.ckFlexObserver = new MutationObserver(() => applyChain());
      this.ckFlexObserver.observe(host, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
    }

    setTimeout(() => this.tour?.startIfFirstVisit(), 250);
  }

  onEditorChange(html: string): void {
    this.htmlChange$.next(html);
  }

  onFieldInserted(path: string): void {
    if (this.editorInstance) {
      insertPlaceholderAtCursor(this.editorInstance, path);
    }
  }

  onWidgetPropertiesChanged(change: WidgetPropertyChange): void {
    this.widgetProperties = change.properties;
  }

  onPrintOptionsChanged(opts: TemplatePrintOptions): void {
    this.localPrintOptions = opts;
    this.event.emit({ kind: ReportEditorEventKind.PrintOptionsChange, printOptions: opts });
    const currentHtml = this.editorInstance?.getData() ?? this.editorHtml;
    this.htmlChange$.next(currentHtml);
  }

  toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  onTogglePreview(): void {
    this.event.emit({ kind: ReportEditorEventKind.TogglePreview });
  }

  save(): void {
    const currentHtml = this.editorInstance?.getData() ?? this.editorHtml;
    const storageHtml = stripChipMarkupForStorage(currentHtml);
    if (this.rejectUnresolvedPlaceholders(storageHtml)) return;
    this.event.emit({
      kind: ReportEditorEventKind.Saved,
      payload: {
        layoutHtml: storageHtml,
        printOptions: this.localPrintOptions ?? this.config.templateJson.printOptions
      }
    });
  }

  private rejectUnresolvedPlaceholders(storageHtml: string): boolean {
    const context = this.config.templateContext;
    if (!context) return false;

    const { unresolvedPaths } = this.templateProcessor.render(storageHtml, context);
    if (!unresolvedPaths.length) return false;

    this.validationIssues = mergeUnresolvedIssues(this.validationIssues, unresolvedPaths);
    this.toastService.showWarning('Fix the unresolved placeholders before saving.', true);
    this.cdr.markForCheck();
    return true;
  }

  discard(): void {
    this.event.emit({ kind: ReportEditorEventKind.Discarded });
  }

  requestPdf(): void {
    this.event.emit({ kind: ReportEditorEventKind.GeneratePdf });
  }

  get hasValidationIssues(): boolean {
    return this.validationIssues.length > 0;
  }
}
